import {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApiEu} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, TripTagInfo} from '@di-shared/api/di/__generated_eu';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface HistoryTagSelectBottomSheetData {
  tagNames: Array<string>;
}

interface TagAreaProps {
  selectedTagNames: Array<string>;
  onChangeTagNames(tagNames: Array<string>): void;
}

export default function HistoryTagSelectBottomSheet() {
  const initial = (window as any).data as HistoryTagSelectBottomSheetData;
  const {t} = useInferableTranslation();
  const [selectedTagNames, setSelectedTagNames] = useState<Array<string>>(initial.tagNames);

  return (
    <div
      className="bottom_sheet tag_select"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bottomSheetTitle"
    >
      <div className="bottom_sheet_header">
        <h2
          id="bottomSheetTitle"
          className="bottom_sheet_title"
        >
          {t('ctn_mytrips_details_tags_title_eu')}
        </h2>
        <button
          type="button"
          className="btn btn_ic"
          aria-label={t('ctn_home_close')}
          onClick={() => window.bottomSheetClose()}
        >
          <span
            className="ic ic_close_20px"
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="bottom_sheet_inner">
        <div className="bottom_sheet_contents">
          <ErrorBoundary fallback={<TagAreaErrorFallback />}>
            <Suspense fallback={<TagAreaLoading />}>
              <TagArea
                selectedTagNames={selectedTagNames}
                onChangeTagNames={setSelectedTagNames}
              />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className="bottom_sheet_footer">
          <button
            type="button"
            className="btn btn_large btn_filled"
            onClick={() => window.bottomSheetClose(selectedTagNames)}
          >
            {t('ctn_mytrips_filter_apply_eu')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TagArea({selectedTagNames, onChangeTagNames}: TagAreaProps) {
  const {t} = useInferableTranslation();

  const {data: tripTags} = useSuspenseQuery<ApiResponseTripTagListResponse, Error, Array<TripTagInfo>>({
    queryKey: [getQueryKey(diApiEu.tripTags.getAllTags)],
    queryFn: () => diApiEu.tripTags.getAllTags(),
    select: ({data}) => data.tags,
  });

  const isAllTagsSelected = selectedTagNames.length === 0;

  function toggleTagSelection(tagName: string) {
    if (selectedTagNames.length === 0) {
      onChangeTagNames([tagName]);
      return;
    }

    if (selectedTagNames.includes(tagName)) {
      const nextTagNames = selectedTagNames.filter((name) => name !== tagName);

      onChangeTagNames(nextTagNames.length > 0 ? nextTagNames : []);
      return;
    }

    onChangeTagNames([...selectedTagNames, tagName]);
  }

  return (
    <div className="tag_area">
      <button
        type="button"
        className={clsx('btn btn_round', {'is-selected': isAllTagsSelected})}
        onClick={() => onChangeTagNames([])}
      >
        <span
          className="ic ic_check_16px"
          aria-hidden="true"
        />
        {t('ctn_mytrips_download_tags_all_eu')}
      </button>
      {tripTags.map(({tagName}) => (
        <button
          key={tagName}
          type="button"
          className={clsx('btn btn_round', {'is-selected': selectedTagNames.includes(tagName)})}
          onClick={() => toggleTagSelection(tagName)}
        >
          <span
            className="ic ic_check_16px"
            aria-hidden="true"
          />
          {tagName}
        </button>
      ))}
    </div>
  );
}

function TagAreaLoading() {
  return (
    <div className="tag_area">
      {Array.from({length: 4}).map((_, index) => (
        <span
          key={`tag-select-skeleton-${index}`}
          className="btn btn_round skeleton"
          style={{width: 72, height: 32}}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function TagAreaErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <div className="trip_info">
      <div className="empty_area">
        <span className="ic ic_caution_32px"></span>
        <span
          className="body_small_r"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_tags_failed_to_load_eu') + '\n' + t('msg_mytrips_details_try_again_eu')}
        </span>
      </div>
    </div>
  );
}
